import {
  executionRootId,
  executionRootItemId,
  WORKBOOK_MAX_PROCESS_STEPS,
  type WorkbookDefinition,
} from "@erp/shared-types";
import { workbookExecutionOrder } from "./executionOrder";

export type ProcessStep = WorkbookDefinition["processSteps"][number];

/**
 * The Outline node standing for one Worksheet. Never persisted. Sources have
 * none: they are the implied inputs of the Worksheets that read them. Outputs
 * have none either: they read Results and compute nothing.
 */
export interface ExecutionRoot {
  kind: "sheet";
  id: string;
  itemId: string;
  parentId: null;
  depth: 0;
}

export interface OutlineInstruction {
  kind: "instruction";
  id: string;
  parentId: string | null;
  depth: number;
  step: ProcessStep;
}

export type OutlineNode = ExecutionRoot | OutlineInstruction;

/** One Execution root, unassigned Instruction or Data note (the head) and everything nested under it. */
export interface OutlineCell {
  head: OutlineNode;
  /** Everything nested under the head, in pre-order. */
  body: readonly OutlineInstruction[];
  /** Every Instruction in the cell: the head first when it is one, then the body. */
  instructions: readonly OutlineInstruction[];
}

/** A pure structural edit to the Outline. */
export type OutlineOp =
  /**
   * Without `afterId`, a child goes first under its Parent; an unassigned one goes
   * last. `itemIds` links the new step, so inserting one linked only to data adds a Data note.
   */
  | {
      type: "insert";
      id: string;
      parentId: string | null;
      afterId?: string;
      itemIds?: readonly string[];
    }
  /** Swaps with the neighbouring sibling (a Data note: one on the same Data item); children stay attached. */
  | { type: "move"; id: string; direction: "up" | "down" }
  /** Nests under the previous node at the same level (a Data note: the previous one on its Data item). */
  | { type: "indent"; id: string }
  /** Moves up to the Parent's Parent; from an Execution root, to no Parent (unassigned, or a Data note). */
  | { type: "outdent"; id: string }
  /** Children move up to the removed step's Parent. */
  | { type: "remove"; id: string }
  /** Before an Item is deleted: unlink it and unassign the Instructions under its root. */
  | { type: "detachItem"; itemId: string };

export interface ProcessOutline {
  /** Pre-order: Execution roots in Execution order, unassigned Instructions, then Data notes. */
  readonly nodes: readonly OutlineNode[];
  /** The Process's cells: Execution roots, then unassigned Instructions. */
  readonly cells: readonly OutlineCell[];
  /** The Data notes on the Data item `itemId` (a File or Source), each as its cell. */
  dataNotes(itemId: string): readonly OutlineCell[];
  /** The Data item the step is a Data note on, or undefined when it is not one. */
  dataItemOf(stepId: string): string | undefined;
  node(id: string): OutlineNode | undefined;
  /** The steps whose Parent is `parentId` (null: unassigned), in sibling order. */
  children(parentId: string | null): readonly ProcessStep[];
  /** The step's Linked items: its Items plus those of its ancestors. */
  linkedItemIds(stepId: string): ReadonlySet<string>;
  /** Whether `op` would change the Definition. */
  can(op: OutlineOp): boolean;
  /** The Process holds as many steps as a Definition allows; nothing more can be inserted. */
  readonly full: boolean;
  /** Steps no root reaches: a missing Parent or a cycle. Parsing rejects both. */
  readonly unreachable: readonly string[];
}

type StepsChange = (steps: readonly ProcessStep[]) => ProcessStep[];

function patch(id: string, fields: Partial<ProcessStep>): StepsChange {
  return (steps) =>
    steps.map((step) => (step.id === id ? { ...step, ...fields } : step));
}

function swap(first: string, second: string): StepsChange {
  return (steps) => {
    const next = [...steps];
    const a = next.findIndex((step) => step.id === first);
    const b = next.findIndex((step) => step.id === second);
    const [left, right] = [next[a], next[b]];
    if (!left || !right) return next;
    [next[a], next[b]] = [right, left];
    return next;
  };
}

function groupByParent(steps: readonly ProcessStep[]) {
  const children = new Map<string | null, ProcessStep[]>();
  for (const step of steps) {
    const siblings = children.get(step.parentId);
    if (siblings) siblings.push(step);
    else children.set(step.parentId, [step]);
  }
  return children;
}

function preOrder(
  roots: readonly ExecutionRoot[],
  children: ReadonlyMap<string | null, readonly ProcessStep[]>,
  isDataNote: (step: ProcessStep) => boolean,
) {
  const nodes: OutlineNode[] = [];
  const byId = new Map<string, OutlineNode>();
  function visit(node: OutlineNode) {
    if (byId.has(node.id)) return;
    nodes.push(node);
    byId.set(node.id, node);
    for (const step of children.get(node.id) ?? [])
      visit(instruction(step, node.depth + 1));
  }
  for (const root of roots) visit(root);
  const unassigned = children.get(null) ?? [];
  for (const step of unassigned.filter((item) => !isDataNote(item)))
    visit(instruction(step, 0));
  for (const step of unassigned.filter(isDataNote)) visit(instruction(step, 0));
  return { nodes, byId };
}

function instruction(step: ProcessStep, depth: number): OutlineInstruction {
  return {
    kind: "instruction",
    id: step.id,
    parentId: step.parentId,
    depth,
    step,
  };
}

function cellsOf(nodes: readonly OutlineNode[]): OutlineCell[] {
  const cells: { head: OutlineNode; body: OutlineInstruction[] }[] = [];
  for (const node of nodes) {
    if (node.depth === 0) cells.push({ head: node, body: [] });
    else if (node.kind === "instruction") cells.at(-1)?.body.push(node);
  }
  return cells.map(({ head, body }) => ({
    head,
    body,
    instructions: head.kind === "instruction" ? [head, ...body] : body,
  }));
}

/**
 * A Data note's Data item: its first linked Source, else its first linked File;
 * undefined when the step is not a Data note. A Data note is an Instruction with
 * no Parent whose Items are all Files or Sources.
 */
function dataNoteHome(definition: WorkbookDefinition) {
  const sources = new Set(definition.sources.map((source) => source.id));
  const files = new Set(definition.files.map((file) => file.id));
  return (step: ProcessStep): string | undefined => {
    if (step.parentId !== null || !step.itemIds.length) return undefined;
    if (!step.itemIds.every((id) => sources.has(id) || files.has(id)))
      return undefined;
    return step.itemIds.find((id) => sources.has(id)) ?? step.itemIds[0];
  };
}

function build(definition: WorkbookDefinition) {
  const steps = definition.processSteps;
  const stepsById = new Map(steps.map((step) => [step.id, step]));
  const children = groupByParent(steps);
  const roots = workbookExecutionOrder(definition).flatMap(
    (item): ExecutionRoot[] =>
      item.kind === "source"
        ? []
        : [
            {
              kind: "sheet",
              id: executionRootId(item.id),
              itemId: item.id,
              parentId: null,
              depth: 0,
            },
          ],
  );
  const itemIds = new Set([
    ...definition.files.map((file) => file.id),
    ...definition.sources.map((source) => source.id),
    ...definition.sheets.map((sheet) => sheet.id),
    ...definition.outputs.map((output) => output.id),
  ]);
  const home = dataNoteHome(definition);
  const isDataNote = (step: ProcessStep) => home(step) !== undefined;
  const { nodes, byId } = preOrder(roots, children, isDataNote);
  const childrenOf = (parentId: string | null) => children.get(parentId) ?? [];
  /** A step's siblings: under its Parent, or its fellow unassigned Instructions or Data notes on the same Data item. */
  const siblingsOf = (step: ProcessStep) =>
    step.parentId !== null
      ? childrenOf(step.parentId)
      : childrenOf(null).filter((item) => home(item) === home(step));
  const full = steps.length >= WORKBOOK_MAX_PROCESS_STEPS;
  const reachable = (id: string) => {
    const node = byId.get(id);
    return node?.kind === "instruction" ? node : undefined;
  };

  function insert(op: Extract<OutlineOp, { type: "insert" }>) {
    if (full || stepsById.has(op.id)) return null;
    if (op.parentId !== null && !byId.has(op.parentId)) return null;
    if (op.afterId && stepsById.get(op.afterId)?.parentId !== op.parentId)
      return null;
    if (op.itemIds?.some((itemId) => !itemIds.has(itemId))) return null;
    const { afterId, parentId } = op;
    const change: StepsChange = (current) => {
      const next = [...current];
      const firstChild =
        parentId === null
          ? -1
          : next.findIndex((step) => step.parentId === parentId);
      const index = afterId
        ? next.findIndex((step) => step.id === afterId) + 1
        : firstChild >= 0
          ? firstChild
          : next.length;
      next.splice(index, 0, {
        id: op.id,
        parentId,
        instructions: "",
        itemIds: [...(op.itemIds ?? [])],
      });
      return next;
    };
    return change;
  }

  function move(id: string, direction: "up" | "down") {
    const node = reachable(id);
    if (!node) return null;
    const siblings = siblingsOf(node.step);
    const position = siblings.findIndex((step) => step.id === id);
    const adjacent = siblings[position + (direction === "up" ? -1 : 1)];
    return adjacent ? swap(id, adjacent.id) : null;
  }

  function indent(id: string) {
    const node = reachable(id);
    if (!node) return null;
    // An unassigned Instruction's previous node may be an Execution root.
    const level: readonly { id: string }[] =
      node.parentId === null && !isDataNote(node.step)
        ? [...roots, ...siblingsOf(node.step)]
        : siblingsOf(node.step);
    const previous = level[level.findIndex((item) => item.id === id) - 1];
    return previous ? patch(id, { parentId: previous.id }) : null;
  }

  function outdent(id: string) {
    const node = reachable(id);
    if (!node?.parentId) return null;
    return patch(id, { parentId: byId.get(node.parentId)?.parentId ?? null });
  }

  function remove(id: string) {
    const step = stepsById.get(id);
    if (!step) return null;
    const change: StepsChange = (current) =>
      current
        .filter((item) => item.id !== id)
        .map((item) =>
          item.parentId === id ? { ...item, parentId: step.parentId } : item,
        );
    return change;
  }

  function detachItem(itemId: string) {
    const rootId = executionRootId(itemId);
    const linked = (step: ProcessStep) =>
      step.parentId === rootId || step.itemIds.includes(itemId);
    if (!steps.some(linked)) return null;
    const change: StepsChange = (current) =>
      current.map((step) =>
        linked(step)
          ? {
              ...step,
              parentId: step.parentId === rootId ? null : step.parentId,
              itemIds: step.itemIds.filter((id) => id !== itemId),
            }
          : step,
      );
    return change;
  }

  function plan(op: OutlineOp): StepsChange | null {
    switch (op.type) {
      case "insert":
        return insert(op);
      case "move":
        return move(op.id, op.direction);
      case "indent":
        return indent(op.id);
      case "outdent":
        return outdent(op.id);
      case "remove":
        return remove(op.id);
      case "detachItem":
        return detachItem(op.itemId);
    }
  }

  function linkedItemIds(stepId: string) {
    const ids = new Set<string>();
    const seen = new Set<string>();
    let current: string | null = stepId;
    while (current !== null && !seen.has(current)) {
      seen.add(current);
      const step = stepsById.get(current);
      if (!step) {
        const itemId = executionRootItemId(current);
        if (itemId) ids.add(itemId);
        break;
      }
      for (const id of step.itemIds) ids.add(id);
      current = step.parentId;
    }
    return ids;
  }

  const allCells = cellsOf(nodes);
  const isDataNoteCell = (cell: OutlineCell) =>
    cell.head.kind === "instruction" && isDataNote(cell.head.step);
  const outline: ProcessOutline = {
    nodes,
    cells: allCells.filter((cell) => !isDataNoteCell(cell)),
    dataNotes: (itemId) =>
      allCells.filter(
        (cell) =>
          cell.head.kind === "instruction" && home(cell.head.step) === itemId,
      ),
    dataItemOf: (stepId) => {
      const step = stepsById.get(stepId);
      return step && home(step);
    },
    node: (id) => byId.get(id),
    children: childrenOf,
    linkedItemIds,
    can: (op) => plan(op) !== null,
    full,
    unreachable: steps
      .filter((step) => !byId.has(step.id))
      .map((step) => step.id),
  };
  return { outline, plan };
}

/** Builds the Outline of a Definition once; read everything about the Process from it. */
export function processOutline(definition: WorkbookDefinition): ProcessOutline {
  return build(definition).outline;
}

/** Applies `op`, returning `definition` itself when the op changes nothing. */
export function applyOutlineOp(
  definition: WorkbookDefinition,
  op: OutlineOp,
): WorkbookDefinition {
  const change = build(definition).plan(op);
  return change
    ? { ...definition, processSteps: change(definition.processSteps) }
    : definition;
}
